import { useLayoutEffect, useState } from "react";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import {
  BREAKPOINT_MOBILE,
  GRID_GAP,
  TILE_DECORATOR_HEIGHT,
  TILE_WIDTH_PX_LG,
  TILE_WIDTH_PX_SM,
  useBreakpoints,
} from "constants/constants";
import { useVisibleProcesses } from "utils/customHooks/genericHooks";
import useWindowSize from "utils/customHooks/useWindowSize";
import { getExpandedTileContentHeights } from "components/Dashboard/Tiles/ProcessTile/ExpandedTile/ExpandedTileModules/ExpandedTileModules";
import { useExpandedTileFlags } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export const TILE_DIVIDER_HEIGHT = 1;

/**
 * returns tile width and height,
 * tile width depends whether you've activated "isLargeTiles" mode;
 * tile height depends on whether the process tile is expanded, and which expanded-tiles features are active
 */
export function useTileSize() {
  const { tileWidth: dynamicWidth } = useMaxSquareWidth();
  const { isDesktopXlOrLarger } = useBreakpoints();
  const xlScale = isDesktopXlOrLarger
    ? // ! disabled because sbdwarrington likes to view all their 100+ tiles at once on a large screen
      // ? getScaleFromWidthForLargeScreens(innerWidth)
      2
    : 1;

  const { isLargeTiles, tileWidthMultiplier, tileScaleMultiplier } =
    useSettings();
  // For width, apply dynamicWidth in large-tiles mode (with no multipliers yet)
  const tileWidthControlled = isLargeTiles
    ? dynamicWidth
    : TILE_WIDTH_PX_SM * xlScale;

  // For height, in large-tiles mode use dynamicWidth without the tileWidthMultiplier
  // This ensures the height is based on the original square dimension
  const tileHeightControlled = isLargeTiles
    ? dynamicWidth
    : TILE_WIDTH_PX_SM * xlScale;

  const { tileHeightMultiplier } = useExpandedTiles(dynamicWidth);

  const visibleProcesses = useVisibleProcesses();
  const areAnyTileDecoratorsVisible = Boolean(
    visibleProcesses.find(p => Boolean(p.lineRole))
  );

  const tileDecoratorHeight = TILE_DECORATOR_HEIGHT * (isLargeTiles ? 2 : 1);

  // Apply tileWidthMultiplier to width only (width-specific scaling)
  // Apply tileScaleMultiplier to both width and height (uniform scaling)
  return {
    tileWidth: tileWidthControlled * tileWidthMultiplier * tileScaleMultiplier,
    tileHeight: tileHeightControlled * tileScaleMultiplier,
    tileAndContentsHeight:
      tileHeightControlled * tileHeightMultiplier +
      (areAnyTileDecoratorsVisible ? tileDecoratorHeight : 0),
    isLargeTiles,
  };
}

export function useExpandedTiles(tileWidth: number) {
  const {
    expandedProcessTileConfig: {
      isOperatorNameVisible,
      isTileButterflyVisible,
      isSkuNameVisible,
      isSkuIdVisible,
      isTileCountVisible,
      isTileMetricPercentVisible,
      isTileAheadBehindVisible,
      isTeamSizeVisible,
      isTopLossesVisible,
    },
    areProcessTilesExpanded,
    isLargeTiles,
    tileScaleMultiplier,
  } = useSettings();
  const { isAnyTileAheadBehindAllowed } = useExpandedTileFlags(null);

  const [
    {
      general: {
        expandedProcessTileOperatorName,
        expandedProcessTileSkuName,
        expandedProcessTileSkuId,
        expandedProcessTileCompactMode,
        expandedProcessTileTeamSize,
      },
    },
  ] = useFlightConfig();

  const showOperatorModule =
    isOperatorNameVisible && expandedProcessTileOperatorName;
  const showSkuName = isSkuNameVisible && expandedProcessTileSkuName;
  const showSkuId = isSkuIdVisible && expandedProcessTileSkuId;
  const showSkuModule = showSkuName || showSkuId;

  const largeOrSmall = isLargeTiles ? "lg" : "sm";

  const visibleProcesses = useVisibleProcesses();
  const isAtLeastOneProcessWithLineRole = Boolean(
    visibleProcesses.find(p => Boolean(p.lineRole))
  );

  // each component adds a unique height (px) to the expanded tile height
  const componentsWithHeights: [boolean, number][] = [
    [
      showOperatorModule,
      getExpandedTileContentHeights(expandedProcessTileCompactMode).singleLine[
        largeOrSmall
      ] + TILE_DIVIDER_HEIGHT,
    ],
    [
      showSkuModule,
      getExpandedTileContentHeights(expandedProcessTileCompactMode)[
        showSkuName && showSkuId ? "doubleLine" : "singleLine"
      ][largeOrSmall] + TILE_DIVIDER_HEIGHT,
    ],
    [
      isTileMetricPercentVisible,
      getExpandedTileContentHeights(expandedProcessTileCompactMode).singleLine[
        largeOrSmall
      ] + TILE_DIVIDER_HEIGHT,
    ],
    [
      isTileCountVisible &&
        !isAtLeastOneProcessWithLineRole && // space is already added if there's a process with a lineRole
        !(isAnyTileAheadBehindAllowed && isTileAheadBehindVisible), // only show count if no ahead/behind (includes count)
      getExpandedTileContentHeights(expandedProcessTileCompactMode).singleLine[
        largeOrSmall
      ] + TILE_DIVIDER_HEIGHT,
    ],
    [
      isTileAheadBehindVisible && isAnyTileAheadBehindAllowed,
      getExpandedTileContentHeights(expandedProcessTileCompactMode)
        .tileAheadBehind[largeOrSmall] + TILE_DIVIDER_HEIGHT,
    ],
    [
      isTeamSizeVisible,
      getExpandedTileContentHeights(expandedProcessTileTeamSize).teamSize[
        largeOrSmall
      ] + TILE_DIVIDER_HEIGHT,
    ],
    [
      isTileButterflyVisible,
      getExpandedTileContentHeights(expandedProcessTileCompactMode)
        .tileButterfly[largeOrSmall] + (isLargeTiles ? 6 : 3),
    ],
    [
      isTopLossesVisible,
      getExpandedTileContentHeights(expandedProcessTileCompactMode).topLosses[
        largeOrSmall
      ],
    ],
  ];

  // Scale component heights based on tileScaleMultiplier
  const expandedTileHeight =
    componentsWithHeights.reduce(
      (acc, [isVisible, componentHeight]) =>
        acc + Number(isVisible) * componentHeight,
      0
    ) + (isLargeTiles ? 4 : 2);

  // When calculating the percentage, we need to account for the scale multiplier
  // tileWidth already includes tileScaleMultiplier, so we're accounting for it here
  const baseTileSize = isLargeTiles ? tileWidth : TILE_WIDTH_PX_SM;
  const expandedTileHeightPct = expandedTileHeight / baseTileSize;

  const tileHeightMultiplier =
    tileScaleMultiplier + (areProcessTilesExpanded ? expandedTileHeightPct : 0);
  return { tileHeightMultiplier };
}

function useMaxSquareWidth(): { tileWidth: number } {
  const { innerWidth, innerHeight } = useWindowSize();
  const heightAboveTiles = useHeightAboveTiles();
  const processes = useVisibleProcesses();
  return getMaxSquareWidth({
    panelHeight: innerHeight - heightAboveTiles,
    panelWidth: innerWidth,
    numTiles: processes.length,
  });
}

export const TILES_CARD = "tilesCard";
const APPROX_HEIGHT_ABOVE_TILES = 290; // estimate the starting position; this will update so it should only affect the first flicker

/** get the height above the tilesCard so we can calculate the maximum possible tile size */
export function useHeightAboveTiles(): number {
  const [heightAboveTiles, setHeightAboveTiles] = useState(
    APPROX_HEIGHT_ABOVE_TILES
  );

  useLayoutEffect(() => {
    const tilesCard = document.querySelector(`.${TILES_CARD}`);
    if (!tilesCard) {
      return;
    }
    const bbox = tilesCard.getBoundingClientRect();
    if (heightAboveTiles !== bbox.top) {
      setHeightAboveTiles(bbox.top);
    }
  }, [heightAboveTiles, setHeightAboveTiles]);

  return heightAboveTiles;
}

export function getMaxSquareWidth({
  panelHeight,
  panelWidth,
  numTiles,
  tileWidthConfig,
}: {
  panelHeight: number;
  panelWidth: number;
  numTiles: number;
  tileWidthConfig?: number;
}): { tileWidth: number; numRows: number } {
  // if there are 21 or more tiles (3 rows of 7), return 215px
  if (numTiles > 20 || panelWidth < BREAKPOINT_MOBILE) {
    return { tileWidth: (tileWidthConfig ?? 1) * TILE_WIDTH_PX_LG, numRows: 0 };
  }

  const isWindowWide = panelWidth > panelHeight;
  const shortestLength = isWindowWide ? panelHeight : panelWidth;

  let numRows = 1;
  // start squareWidth at maximum, then decrease until all squares fit
  let squareWidth = shortestLength - GRID_GAP; // grid gap on both outer sides
  let numTilesPerRow = Math.ceil(numTiles / numRows);
  // if the squares overflow the width OR the height
  while (
    // doSquaresOverflowWidth
    numTilesPerRow * (squareWidth + GRID_GAP) > panelWidth ||
    // doSquaresOverflowHeight
    numRows * (squareWidth + GRID_GAP) > panelHeight
  ) {
    // shrink squares until they fit
    squareWidth -= 1;

    // add a row if there's room
    const isroomForAnotherRow = Math.floor(panelHeight / squareWidth) > numRows;
    if (isroomForAnotherRow) {
      numRows += 1;
      numTilesPerRow = Math.ceil(numTiles / numRows);
    }
  }

  return {
    tileWidth: squareWidth - GRID_GAP,
    numRows,
  };
}
